import {
    zoomOutScaleUpdate,
    zoomInScaleUpdate,
    showZoomCallout,
} from 'owa-custom-zoom-store/lib/mutators/zoomMutators';

interface TouchPoint {
    x: number;
    y: number;
}

interface PinchState {
    initialDistance: number;
    lastDistance: number;
    isActive: boolean;
    threshold: number; // Minimum distance change to trigger zoom in px
}

const pinchState: PinchState = {
    initialDistance: 0,
    lastDistance: 0,
    isActive: false,
    threshold: 10,
};

/**
 * Calculate the distance between two touch points
 */
function getDistance(touch1: TouchPoint, touch2: TouchPoint): number {
    const dx = touch1.x - touch2.x;
    const dy = touch1.y - touch2.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get touch point coordinates from a Touch object
 */
function getTouchPoint(touch: Touch): TouchPoint {
    return {
        x: touch.clientX,
        y: touch.clientY,
    };
}

/**
 * Handle touch start event for pinch zoom
 */
export function onPinchZoomStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
        event.preventDefault();

        const touch1 = getTouchPoint(event.touches[0]);
        const touch2 = getTouchPoint(event.touches[1]);
        const distance = getDistance(touch1, touch2);

        pinchState.initialDistance = distance;
        pinchState.lastDistance = distance;
        pinchState.isActive = true;

        // Show zoom control for showing the user zoom level changes
        showZoomCallout('default');
    }
}

/**
 * Handle touch move event for pinch zoom
 */
export function onPinchZoomMove(event: TouchEvent): void {
    if (!pinchState.isActive || event.touches.length !== 2) {
        return;
    }

    event.preventDefault();

    const touch1 = getTouchPoint(event.touches[0]);
    const touch2 = getTouchPoint(event.touches[1]);
    const currentDistance = getDistance(touch1, touch2);

    const distanceChange = currentDistance - pinchState.lastDistance;

    // Only trigger zoom if the distance change exceeds the threshold
    if (Math.abs(distanceChange) > pinchState.threshold) {
        if (distanceChange > 0) {
            // Fingers moving apart - zoom in
            zoomInScaleUpdate();
        } else {
            // Fingers moving together - zoom out
            zoomOutScaleUpdate();
        }

        pinchState.lastDistance = currentDistance;
    }
}

/**
 * Handle touch end event for pinch zoom
 */
export function onPinchZoomEnd(event: TouchEvent): void {
    // Reset pinch state when fingers are lifted
    if (event.touches.length < 2) {
        resetPinchZoom();
    }
}

/**
 * Reset pinch zoom state (for cleanup)
 */
export function resetPinchZoom(): void {
    pinchState.isActive = false;
    pinchState.initialDistance = 0;
    pinchState.lastDistance = 0;
}
